import {
  BlockerKind,
  BoardSnapshot,
  CellState,
  Coord,
  LevelSpec,
  MatchGroup,
  MoveEvent,
  ObjectiveState,
  Piece,
  PieceType,
  SpecialKind,
  TurnResult,
  PIECE_COLORS,
} from './types';

/**
 * Options intentionally stay data-only.  The renderer can create an engine with
 * a deterministic fixture board without teaching the logical model anything
 * about Three.js objects.
 */
export interface BoardEngineOptions {
  seed?: number;
  initialPieces?: (PieceType | Piece | null)[][];
  /** Keep a fixture board exactly as supplied instead of generating a playable one. */
  ensurePlayable?: boolean;
  /** Maximum number of match waves resolved by one player action. */
  maxCascade?: number;
}

type PieceInput = PieceType | Piece | null | undefined;

interface InternalCell extends CellState {
  /** Original explicit lock state, so destroying a lock blocker does not unlock a hand-locked cell. */
  explicitLocked: boolean;
}

interface InternalState {
  cells: InternalCell[][];
  pieces: (Piece | null)[][];
  objectives: ObjectiveState[];
  score: number;
  movesRemaining: number;
  cascade: number;
  rngState: number;
  nextPieceId: number;
}

const PIECE_TYPES: PieceType[] = [0, 1, 2, 3, 4, 5, 6];
const SPECIAL_NONE: SpecialKind = 'none';
const DEFAULT_MAX_CASCADE = 64;
const MAX_GENERATION_ATTEMPTS = 192;
const MAX_RESHUFFLE_ATTEMPTS = 256;
const MAX_SETTLE_PASSES = 96;
const MAX_RESIDUAL_REPAIR_PASSES = 12;

const DIRECTIONS: Coord[] = [
  { r: -1, c: 0 },
  { r: 0, c: 1 },
  { r: 1, c: 0 },
  { r: 0, c: -1 },
];

const SPECIAL_KINDS: SpecialKind[] = ['beamH', 'beamV', 'nova', 'prism', 'burst'];

function coordKey(coord: Coord): string {
  return `${coord.r},${coord.c}`;
}

function parseCoordKey(key: string): Coord | null {
  const match = key.trim().match(/^(\d+)\s*[,;:_-]\s*(\d+)$/);
  if (!match) return null;
  return { r: Number(match[1]), c: Number(match[2]) };
}

function cloneCoord(coord: Coord): Coord {
  return { r: coord.r, c: coord.c };
}

function isPieceType(value: unknown): value is PieceType {
  return typeof value === 'number' && Number.isInteger(value) && value >= 0 && value <= 6;
}

function normalizeSeed(seed: number): number {
  const normalized = (Number.isFinite(seed) ? Math.trunc(seed) : 1) >>> 0;
  return normalized === 0 ? 0x6d2b79f5 : normalized;
}

function hashSeed(seed: number, salt: number): number {
  let value = (seed ^ Math.imul(salt + 1, 0x9e3779b9)) >>> 0;
  value ^= value >>> 16;
  value = Math.imul(value, 0x85ebca6b) >>> 0;
  value ^= value >>> 13;
  value = Math.imul(value, 0xc2b2ae35) >>> 0;
  value ^= value >>> 16;
  return value === 0 ? 0x6d2b79f5 : value;
}

/**
 * Deterministic logical match-3 simulation for Lumenwild.
 *
 * The class owns no render objects and emits only serializable board snapshots
 * and semantic events.  Every random decision goes through the local xorshift
 * generator, which makes fixtures, replays, simulations and bug reports repeatable.
 */
export class BoardEngine {
  public readonly level: LevelSpec;
  public readonly width: number;
  public readonly height: number;
  public readonly seed: number;

  public cells: CellState[][];
  public pieces: (Piece | null)[][];
  public objectives: ObjectiveState[];
  public score = 0;
  public movesRemaining: number;
  public cascade = 0;

  /** Runtime-facing aliases used by the scene and campaign simulator. */
  public get moves(): number {
    return this.movesRemaining;
  }

  public get phase(): 'ready' | 'won' | 'lost' {
    if (this.areObjectivesComplete()) return 'won';
    if (this.movesRemaining <= 0) return 'lost';
    return 'ready';
  }

  private readonly maxCascade: number;
  private rngState: number;
  private nextPieceId = 1;
  private readonly initialState: InternalState;
  private specialComboCount = 0;

  public constructor(level: LevelSpec, options: BoardEngineOptions | number = {}) {
    const resolvedOptions: BoardEngineOptions =
      typeof options === 'number' ? { seed: options } : options;
    this.level = level;
    this.width = Math.max(1, Math.floor(level.width));
    this.height = Math.max(1, Math.floor(level.height));
    this.seed = normalizeSeed(resolvedOptions.seed ?? level.seed);
    this.rngState = this.seed;
    this.maxCascade = Math.max(1, Math.floor(resolvedOptions.maxCascade ?? DEFAULT_MAX_CASCADE));
    this.movesRemaining = Math.max(0, Math.floor(level.moves));
    this.cells = this.buildCells(level);
    this.pieces = this.emptyPieceGrid();
    this.objectives = (level.objectives ?? []).map((objective) => ({
      ...objective,
      current: 0,
      complete: objective.target <= 0,
    }));

    if (resolvedOptions.initialPieces !== undefined) {
      this.setPieces(resolvedOptions.initialPieces);
      if (resolvedOptions.ensurePlayable === true && this.findMatchGroups().length === 0) {
        this.ensurePlayableFixture();
      }
    } else {
      this.populateInitialBoard();
    }

    this.initialState = this.captureInternalState();
  }

  /** Return a deep, renderer-safe snapshot of the current logical state. */
  public getSnapshot(): BoardSnapshot {
    return {
      width: this.width,
      height: this.height,
      cells: this.cells.map((row) => row.map((cell) => this.copyCellForSnapshot(cell))),
      pieces: this.pieces.map((row) => row.map((piece) => this.copyPiece(piece))),
      objectives: this.objectives.map((objective) => ({ ...objective })),
      score: this.score,
      moves: this.movesRemaining,
      cascade: this.cascade,
      seed: this.seed,
    };
  }

  /** Alias used by UI adapters that prefer state terminology. */
  public getState(): BoardSnapshot {
    return this.getSnapshot();
  }

  /** Alias useful to replay/simulation callers. */
  public snapshot(): BoardSnapshot {
    return this.getSnapshot();
  }

  public getCell(coord: Coord): CellState | null {
    return this.isInside(coord) ? this.cells[coord.r][coord.c] : null;
  }

  public getPiece(coord: Coord): Piece | null {
    return this.isInside(coord) ? this.pieces[coord.r][coord.c] : null;
  }

  /**
   * Install a deterministic fixture board.  This is deliberately a logical API:
   * callers can supply either seven type numbers or fully formed Piece records.
   */
  public setPieces(input: (PieceType | Piece | null)[][]): void {
    const grid = this.emptyPieceGrid();
    let largestId = 0;

    for (let r = 0; r < this.height; r += 1) {
      for (let c = 0; c < this.width; c += 1) {
        const value: PieceInput = input[r]?.[c];
        if (!this.canHoldPiece({ r, c }) || value === null || value === undefined) {
          grid[r][c] = null;
          continue;
        }

        const piece = this.normalizePieceInput(value);
        grid[r][c] = piece;
        largestId = Math.max(largestId, piece.id);
      }
    }

    this.pieces = grid;
    this.nextPieceId = Math.max(this.nextPieceId, largestId + 1);
  }

  /** Convenience alias for test fixtures and deterministic replay loaders. */
  public loadBoard(input: (PieceType | Piece | null)[][]): void {
    this.setPieces(input);
  }

  public setPiece(coord: Coord, input: PieceType | Piece | null): void {
    if (!this.isInside(coord) || !this.canHoldPiece(coord)) return;
    this.pieces[coord.r][coord.c] = input === null ? null : this.normalizePieceInput(input);
    if (this.pieces[coord.r][coord.c]) {
      this.nextPieceId = Math.max(this.nextPieceId, (this.pieces[coord.r][coord.c] as Piece).id + 1);
    }
  }

  /** Find maximal horizontal/vertical and compact 2x2 match groups. */
  public findMatchGroups(): MatchGroup[] {
    const runs: MatchGroup[] = [];

    for (let r = 0; r < this.height; r += 1) {
      let c = 0;
      while (c < this.width) {
        const piece = this.pieces[r][c];
        if (!piece || !this.isMatchableCell({ r, c })) {
          c += 1;
          continue;
        }

        const start = c;
        c += 1;
        while (
          c < this.width &&
          this.isMatchableCell({ r, c }) &&
          this.pieces[r][c] !== null &&
          this.pieces[r][c]!.type === piece.type
        ) {
          c += 1;
        }

        if (c - start >= 3) {
          const cells: Coord[] = [];
          for (let column = start; column < c; column += 1) cells.push({ r, c: column });
          runs.push({ type: piece.type, cells, horizontal: true, vertical: false });
        }
      }
    }

    for (let c = 0; c < this.width; c += 1) {
      let r = 0;
      while (r < this.height) {
        const piece = this.pieces[r][c];
        if (!piece || !this.isMatchableCell({ r, c })) {
          r += 1;
          continue;
        }

        const start = r;
        r += 1;
        while (
          r < this.height &&
          this.isMatchableCell({ r, c }) &&
          this.pieces[r][c] !== null &&
          this.pieces[r][c]!.type === piece.type
        ) {
          r += 1;
        }

        if (r - start >= 3) {
          const cells: Coord[] = [];
          for (let row = start; row < r; row += 1) cells.push({ r: row, c });
          runs.push({ type: piece.type, cells, horizontal: false, vertical: true });
        }
      }
    }

    // A compact four-piece square is an intentional match shape in Lumenwild.
    // It is what makes burst pieces possible without requiring a line.
    for (let r = 0; r < this.height - 1; r += 1) {
      for (let c = 0; c < this.width - 1; c += 1) {
        const square = [
          { r, c },
          { r: r + 1, c },
          { r, c: c + 1 },
          { r: r + 1, c: c + 1 },
        ];
        if (!square.every((cell) => this.isMatchableCell(cell) && this.pieces[cell.r][cell.c] !== null)) {
          continue;
        }
        const type = this.pieces[r][c]!.type;
        if (square.every((cell) => this.pieces[cell.r][cell.c]!.type === type)) {
          runs.push({ type, cells: square, horizontal: false, vertical: false });
        }
      }
    }

    const groups: MatchGroup[] = [];
    for (const run of runs) {
      const overlapping = groups.filter(
        (group) =>
          group.type === run.type &&
          group.cells.some((groupCell) => run.cells.some((runCell) => coordKey(groupCell) === coordKey(runCell))),
      );

      if (overlapping.length === 0) {
        groups.push({
          type: run.type,
          cells: run.cells.map(cloneCoord),
          horizontal: run.horizontal,
          vertical: run.vertical,
        });
        continue;
      }

      const first = groups.indexOf(overlapping[0]);
      const mergedCells = new Map<string, Coord>();
      let horizontal = run.horizontal;
      let vertical = run.vertical;
      for (const group of overlapping) {
        horizontal = horizontal || group.horizontal;
        vertical = vertical || group.vertical;
        for (const cell of group.cells) mergedCells.set(coordKey(cell), cloneCoord(cell));
      }
      for (const cell of run.cells) mergedCells.set(coordKey(cell), cloneCoord(cell));

      for (const group of overlapping) {
        const index = groups.indexOf(group);
        if (index !== first) groups.splice(index, 1);
      }
      groups[first] = {
        type: run.type,
        cells: Array.from(mergedCells.values()).sort((a, b) => a.r - b.r || a.c - b.c),
        horizontal,
        vertical,
      };
    }

    for (const group of groups) {
      group.special = this.specialForGroup(group);
      group.anchor = this.defaultAnchor(group.cells);
    }

    return groups.sort((a, b) => {
      const aAnchor = a.anchor ?? { r: 0, c: 0 };
      const bAnchor = b.anchor ?? { r: 0, c: 0 };
      return aAnchor.r - bAnchor.r || aAnchor.c - bAnchor.c || a.type - b.type;
    });
  }

  /** Short alias retained for simulation callers. */
  public findMatches(): MatchGroup[] {
    return this.findMatchGroups();
  }

  /**
   * Return every legal adjacent swap.  The method only swaps references briefly
   * and never consumes RNG, score, moves or piece ids.
   */
  public findValidMoves(): Coord[][] {
    const valid: Coord[][] = [];
    const seen = new Set<string>();

    for (let r = 0; r < this.height; r += 1) {
      for (let c = 0; c < this.width; c += 1) {
        const from = { r, c };
        if (!this.isSwappableCell(from)) continue;

        const right = { r, c: c + 1 };
        const down = { r: r + 1, c };
        for (const to of [right, down]) {
          if (!this.isSwappableCell(to)) continue;
          const key = `${coordKey(from)}>${coordKey(to)}`;
          if (seen.has(key)) continue;
          seen.add(key);

          const fromPiece = this.pieces[from.r][from.c];
          const toPiece = this.pieces[to.r][to.c];
          this.pieces[from.r][from.c] = toPiece;
          this.pieces[to.r][to.c] = fromPiece;

          const specialMove =
            fromPiece?.special !== SPECIAL_NONE || toPiece?.special !== SPECIAL_NONE;
          const legal = specialMove || this.findMatchGroups().length > 0;

          this.pieces[from.r][from.c] = fromPiece;
          this.pieces[to.r][to.c] = toPiece;

          if (legal) valid.push([cloneCoord(from), cloneCoord(to)]);
        }
      }
    }

    return valid;
  }

  public hasValidMoves(): boolean {
    return this.findValidMoves().length > 0;
  }

  public getValidMoves(): Coord[][] {
    return this.findValidMoves();
  }

  public isValidSwap(from: Coord, to: Coord): boolean {
    return this.findValidMoves().some(
      ([candidateFrom, candidateTo]) =>
        (candidateFrom.r === from.r && candidateFrom.c === from.c && candidateTo.r === to.r && candidateTo.c === to.c) ||
        (candidateFrom.r === to.r && candidateFrom.c === to.c && candidateTo.r === from.r && candidateTo.c === from.c),
    );
  }

  public canSwap(from: Coord, to: Coord): boolean {
    return this.isValidSwap(from, to);
  }

  /** Execute one adjacent swap and, when legal, resolve all deterministic waves. */
  public swap(from: Coord, to: Coord): TurnResult {
    const before = this.getSnapshot();
    const events: MoveEvent[] = [];
    let reshuffled = false;

    if (!this.canAttemptSwap(from, to)) {
      events.push({
        kind: 'invalid',
        cells: this.isInside(from) && this.isInside(to) ? [cloneCoord(from), cloneCoord(to)] : undefined,
        label: this.movesRemaining <= 0 ? 'no moves remaining' : 'swap is not available',
      });
      return this.makeTurnResult(false, false, false, events, before, 0);
    }

    this.swapReferences(from, to);
    events.push({ kind: 'swap', from: cloneCoord(from), to: cloneCoord(to), cells: [cloneCoord(from), cloneCoord(to)] });

    const fromPiece = this.pieces[from.r][from.c];
    const toPiece = this.pieces[to.r][to.c];
    const hasSpecial = fromPiece?.special !== SPECIAL_NONE || toPiece?.special !== SPECIAL_NONE;
    const groups = hasSpecial ? [] : this.findMatchGroups();

    if (!hasSpecial && groups.length === 0) {
      this.swapReferences(from, to);
      events.push({
        kind: 'invalid',
        cells: [cloneCoord(from), cloneCoord(to)],
        from: cloneCoord(from),
        to: cloneCoord(to),
        label: 'that swap does not resonate',
      });
      return this.makeTurnResult(false, false, false, events, before, 0);
    }

    this.movesRemaining = Math.max(0, this.movesRemaining - 1);
    this.advancePieceAges();
    this.updateObjective('survive', 1, events);
    this.cascade = 0;

    if (fromPiece?.special !== SPECIAL_NONE && toPiece?.special !== SPECIAL_NONE) {
      this.resolveSpecialPair(from, to, events);
      this.cascade = 1;
      this.settleBoard(events);
      this.resolveRemainingWaves(events, 1);
    } else if (fromPiece?.special !== SPECIAL_NONE || toPiece?.special !== SPECIAL_NONE) {
      const specialCoord = fromPiece?.special !== SPECIAL_NONE ? from : to;
      const otherCoord = specialCoord === from ? to : from;
      const otherPiece = this.pieces[otherCoord.r][otherCoord.c];
      const plan = new Set<string>([coordKey(specialCoord)]);
      const queue: Coord[] = [cloneCoord(specialCoord)];
      this.cascade = 1;
      this.runClearPlan(plan, queue, events, otherPiece?.type);
      this.settleBoard(events);
      this.resolveRemainingWaves(events, 1);
    } else {
      this.resolveMatchWave(groups, events, [from, to], 1);
      this.resolveRemainingWaves(events, 1);
    }

    this.updateHazards(events);
    this.settleBoard(events);
    // Hazard spread, portal transfer and conveyors can expose a new match even
    // after the ordinary cascade loop has finished.  Always give this final
    // settle pass the same bounded resolver as the primary swap result.
    const postSettleGroups = this.findMatchGroups();
    if (postSettleGroups.length > 0) {
      this.resolveRemainingWaves(events, Math.max(1, this.cascade));
    }

    const won = this.areObjectivesComplete();
    const lost = !won && this.movesRemaining <= 0;

    if (!won && !lost && this.findValidMoves().length === 0) {
      reshuffled = this.reshuffleInternal(events);
    }

    return this.makeTurnResult(true, won, lost, events, before, reshuffled ? 1 : 0);
  }

  /** Aliases make adapters resilient to naming preferences. */
  public trySwap(from: Coord, to: Coord): TurnResult {
    return this.swap(from, to);
  }

  public makeMove(from: Coord, to: Coord): TurnResult {
    return this.swap(from, to);
  }

  public attemptSwap(from: Coord, to: Coord): TurnResult {
    return this.swap(from, to);
  }

  public playSwap(from: Coord, to: Coord): TurnResult {
    return this.swap(from, to);
  }

  /** Revert to the exact deterministic state produced by the constructor. */
  public reset(): void {
    this.restoreInternalState(this.initialState);
  }

  public restart(): void {
    this.reset();
  }

  /** Public safe reshuffle hook for a level UI or a simulation harness. */
  public reshuffle(events: MoveEvent[] = []): boolean {
    return this.reshuffleInternal(events);
  }

  public isStable(): boolean {
    return this.findMatchGroups().length === 0;
  }

  public areObjectivesComplete(): boolean {
    return this.objectives.every((objective) => objective.complete);
  }

  private makeTurnResult(
    accepted: boolean,
    won: boolean,
    lost: boolean,
    events: MoveEvent[],
    before: BoardSnapshot,
    reshuffled: number,
  ): TurnResult {
    return {
      accepted,
      won,
      lost,
      reshuffled: reshuffled > 0,
      events,
      before,
      after: this.getSnapshot(),
      validMoves: this.findValidMoves(),
      movesRemaining: this.movesRemaining,
      scoreGained: this.score - before.score,
    };
  }

  private buildCells(level: LevelSpec): CellState[][] {
    const result: CellState[][] = [];
    for (let r = 0; r < this.height; r += 1) {
      const row: CellState[] = [];
      for (let c = 0; c < this.width; c += 1) {
        const source = this.cellSpecAt(level.cells, r, c);
        const blocker = (source.blocker ?? 'none') as BlockerKind;
        let layers = Math.max(0, Math.floor(source.layers ?? (blocker === 'none' ? 0 : 1)));
        let normalizedBlocker = blocker;
        if (normalizedBlocker === 'none') layers = 0;
        if (normalizedBlocker !== 'none' && layers === 0) layers = 1;
        if (normalizedBlocker === 'lock') layers = Math.max(1, layers);

        const explicitLocked = source.locked === true;
        const cell: InternalCell = {
          ...source,
          active: source.active !== false,
          blocker: normalizedBlocker,
          layers,
          locked: explicitLocked || normalizedBlocker === 'lock',
          hazard: Math.max(0, Math.floor(source.hazard ?? 0)),
          explicitLocked,
        };

        if (!cell.active) {
          cell.blocker = 'none';
          cell.layers = 0;
          cell.locked = false;
          cell.hazard = 0;
        }
        row.push(cell);
      }
      result.push(row);
    }
    return result;
  }

  private cellSpecAt(cells: Record<string, any> | undefined, r: number, c: number): any {
    if (!cells) return {};
    const directKeys = [`${r},${c}`, `${r}:${c}`, `${r}-${c}`, `${r}_${c}`];
    for (const key of directKeys) {
      if (cells[key]) return cells[key];
    }
    for (const [key, value] of Object.entries(cells)) {
      const parsed = parseCoordKey(key);
      if (parsed?.r === r && parsed.c === c) return value;
    }
    return {};
  }

  private emptyPieceGrid(): (Piece | null)[][] {
    return Array.from({ length: this.height }, () => Array<Piece | null>(this.width).fill(null));
  }

  private populateInitialBoard(): void {
    let lastAttemptState = this.seed;
    for (let attempt = 0; attempt < MAX_GENERATION_ATTEMPTS; attempt += 1) {
      this.rngState = hashSeed(this.seed, attempt);
      lastAttemptState = this.rngState;
      this.pieces = this.emptyPieceGrid();
      this.nextPieceId = 1;
      this.fillStableBoard();

      if (this.findMatchGroups().length === 0 && (this.hasValidMoves() || this.countMovablePieces() < 2)) {
        return;
      }
    }

    // A highly constrained shape may have no possible swap at all.  It still
    // receives a match-free board and remains deterministic rather than looping.
    this.rngState = lastAttemptState;
    this.pieces = this.emptyPieceGrid();
    this.nextPieceId = 1;
    this.fillStableBoard();
    this.repairResidualMatches();
  }

  private fillStableBoard(): void {
    for (let r = 0; r < this.height; r += 1) {
      for (let c = 0; c < this.width; c += 1) {
        const coord = { r, c };
        if (!this.canHoldPiece(coord) || this.pieces[r][c] !== null) continue;

        const choices = this.shuffledPieceTypes();
        let chosen = choices[0];
        for (const type of choices) {
          if (!this.wouldCreateMatch(coord, type)) {
            chosen = type;
            break;
          }
        }
        this.pieces[r][c] = this.newPiece(chosen);
      }
    }
  }

  private ensurePlayableFixture(): void {
    if (this.findValidMoves().length > 0) return;
    this.reshuffleInternal([]);
  }

  private shuffledPieceTypes(): PieceType[] {
    const result = PIECE_TYPES.slice();
    this.shuffle(result);
    return result;
  }

  private wouldCreateMatch(coord: Coord, type: PieceType): boolean {
    const same = (r: number, c: number): boolean =>
      this.isInside({ r, c }) &&
      this.isMatchableCell({ r, c }) &&
      this.pieces[r][c] !== null &&
      this.pieces[r][c]!.type === type;

    const horizontal =
      (same(coord.r, coord.c - 1) && same(coord.r, coord.c - 2)) ||
      (same(coord.r, coord.c - 1) && same(coord.r, coord.c + 1)) ||
      (same(coord.r, coord.c + 1) && same(coord.r, coord.c + 2));
    if (horizontal) return true;

    const vertical =
      (same(coord.r - 1, coord.c) && same(coord.r - 2, coord.c)) ||
      (same(coord.r - 1, coord.c) && same(coord.r + 1, coord.c)) ||
      (same(coord.r + 1, coord.c) && same(coord.r + 2, coord.c));
    if (vertical) return true;

    const squareOrigin = [
      { r: coord.r, c: coord.c },
      { r: coord.r - 1, c: coord.c },
      { r: coord.r, c: coord.c - 1 },
      { r: coord.r - 1, c: coord.c - 1 },
    ];
    return squareOrigin.some((origin) => {
      const square = [
        origin,
        { r: origin.r + 1, c: origin.c },
        { r: origin.r, c: origin.c + 1 },
        { r: origin.r + 1, c: origin.c + 1 },
      ];
      return square.every((cell) => {
        if (cell.r === coord.r && cell.c === coord.c) return true;
        return same(cell.r, cell.c);
      });
    });
  }

  private specialForGroup(group: MatchGroup): SpecialKind | undefined {
    if (group.horizontal && group.vertical) return 'nova';
    if (group.cells.length >= 5) return 'prism';
    if (group.cells.length >= 4) {
      const rows = new Set(group.cells.map((cell) => cell.r));
      const columns = new Set(group.cells.map((cell) => cell.c));
      if (rows.size <= 2 && columns.size <= 2) return 'burst';
      return group.horizontal ? 'beamH' : 'beamV';
    }
    return undefined;
  }

  private defaultAnchor(cells: Coord[]): Coord | undefined {
    if (cells.length === 0) return undefined;
    const sorted = cells.slice().sort((a, b) => a.r - b.r || a.c - b.c);
    return cloneCoord(sorted[Math.floor((sorted.length - 1) / 2)]);
  }

  private chooseAnchor(group: MatchGroup, preferred: Coord[]): Coord | undefined {
    for (const candidate of preferred) {
      if (group.cells.some((cell) => cell.r === candidate.r && cell.c === candidate.c)) {
        return cloneCoord(candidate);
      }
    }
    return group.anchor ? cloneCoord(group.anchor) : this.defaultAnchor(group.cells);
  }

  private resolveMatchWave(groups: MatchGroup[], events: MoveEvent[], preferred: Coord[], wave: number): void {
    this.cascade = wave;
    if (wave > 1) events.push({ kind: 'cascade', cascade: wave, amount: wave });
    this.resolveGroups(groups, events, preferred);
    this.settleBoard(events);
  }

  private resolveRemainingWaves(events: MoveEvent[], startingWave: number): void {
    let wave = startingWave;
    let groups = this.findMatchGroups();

    while (groups.length > 0 && wave < this.maxCascade) {
      wave += 1;
      this.resolveMatchWave(groups, events, [], wave);
      groups = this.findMatchGroups();
    }

    this.cascade = Math.max(this.cascade, wave);
    if (groups.length > 0) {
      this.repairResidualMatches(events);
    }
  }

  private resolveGroups(groups: MatchGroup[], events: MoveEvent[], preferred: Coord[]): void {
    const toClear = new Set<string>();
    const specialQueue: Coord[] = [];
    const preserved = new Set<string>();

    for (const group of groups) {
      const special = group.special ?? this.specialForGroup(group);
      const anchor = special ? this.chooseAnchor(group, preferred) : undefined;
      if (special && anchor && this.isInside(anchor)) {
        const anchorPiece = this.pieces[anchor.r][anchor.c];
        if (anchorPiece && anchorPiece.special === SPECIAL_NONE) {
          anchorPiece.special = special;
          preserved.add(coordKey(anchor));
          events.push({
            kind: 'special',
            cells: [cloneCoord(anchor)],
            special,
            label: 'Pattern formed',
          });
        } else if (anchorPiece?.special !== SPECIAL_NONE) {
          specialQueue.push(cloneCoord(anchor));
        }
      }

      for (const cell of group.cells) {
        const key = coordKey(cell);
        if (!preserved.has(key)) toClear.add(key);
        const piece = this.isInside(cell) ? this.pieces[cell.r][cell.c] : null;
        if (piece?.special !== SPECIAL_NONE && !preserved.has(key)) specialQueue.push(cloneCoord(cell));
      }
    }

    this.runClearPlan(toClear, specialQueue, events);
  }

  private resolveSpecialPair(a: Coord, b: Coord, events: MoveEvent[]): void {
    const first = this.pieces[a.r][a.c];
    const second = this.pieces[b.r][b.c];
    if (!first || !second || first.special === SPECIAL_NONE || second.special === SPECIAL_NONE) return;

    const firstKind = first.special;
    const secondKind = second.special;
    const plan = new Set<string>([coordKey(a), coordKey(b)]);
    const queue: Coord[] = [];
    const preActivated = new Set<string>([coordKey(a), coordKey(b)]);
    let targetType: PieceType | undefined;
    let label = `${firstKind} + ${secondKind}`;

    this.specialComboCount += 1;
    this.updateObjective('combo', 1, events);
    this.awardScore(100, events);

    if (firstKind === 'prism' && secondKind === 'prism') {
      label = 'double prism: total constellation';
      this.addEveryPiece(plan);
      this.enqueueSpecialsIn(plan, queue, preActivated);
    } else if (firstKind === 'prism' || secondKind === 'prism') {
      const prism = firstKind === 'prism' ? first : second;
      const other = firstKind === 'prism' ? second : first;
      const otherCoord = firstKind === 'prism' ? b : a;
      targetType = this.choosePrismTarget(other.special === SPECIAL_NONE ? other.type : undefined);
      label = `prism weave: ${other.special}`;

      if (other.special === SPECIAL_NONE) {
        // A prism crossing an ordinary piece erases that colour family.
        for (const cell of this.activeCoordinates()) {
          const piece = this.pieces[cell.r][cell.c];
          if (piece?.type === targetType) {
            plan.add(coordKey(cell));
            if (piece.special !== SPECIAL_NONE) queue.push(cloneCoord(cell));
          }
        }
      } else {
        // Crossing a prism with power weaves a copy of that power through the
        // target colour.  Each woven piece then fires as part of this same plan.
        this.addSpecialPattern(other.special, otherCoord, plan);
        for (const cell of this.activeCoordinates()) {
          const piece = this.pieces[cell.r][cell.c];
          if (!piece || piece.type !== targetType || coordKey(cell) === coordKey(otherCoord)) continue;
          if (piece.special === SPECIAL_NONE) {
            piece.special = other.special;
            events.push({
              kind: 'special',
              cells: [cloneCoord(cell)],
              special: other.special,
              label: `woven ${other.special}`,
            });
          }
          queue.push(cloneCoord(cell));
        }
      }
    } else {
      this.addCombinationPattern(firstKind, secondKind, a, b, plan);
      this.enqueueSpecialsIn(plan, queue, preActivated);
      label = this.combinationLabel(firstKind, secondKind);
    }

    events.push({ kind: 'combo', cells: [cloneCoord(a), cloneCoord(b)], label, amount: 1, cascade: this.cascade });
    this.runClearPlan(plan, queue, events, targetType, preActivated);
  }

  private runClearPlan(
    toClear: Set<string>,
    queue: Coord[],
    events: MoveEvent[],
    prismTarget?: PieceType,
    preActivated: Set<string> = new Set<string>(),
  ): void {
    const activated = new Set<string>(preActivated);
    let activationCount = 0;

    while (queue.length > 0) {
      const coord = queue.shift()!;
      if (!this.isInside(coord)) continue;
      const key = coordKey(coord);
      if (activated.has(key)) continue;
      const piece = this.pieces[coord.r][coord.c];
      if (!piece || piece.special === SPECIAL_NONE) continue;

      activated.add(key);
      activationCount += 1;
      const special = piece.special;
      this.awardScore(25 + this.cascade * 5, events);
      events.push({
        kind: 'special',
        cells: [cloneCoord(coord)],
        special,
        label: `activated ${special}`,
        cascade: this.cascade,
      });

      const before = new Set(toClear);
      if (special === 'prism') {
        const target = this.choosePrismTarget(prismTarget);
        for (const cell of this.activeCoordinates()) {
          const targetPiece = this.pieces[cell.r][cell.c];
          if (!targetPiece || targetPiece.type !== target) continue;
          toClear.add(coordKey(cell));
        }
      } else {
        this.addSpecialPattern(special, coord, toClear);
      }

      const newlyAdded: Coord[] = [];
      for (const addedKey of toClear) {
        if (before.has(addedKey)) continue;
        const added = parseCoordKey(addedKey);
        if (added) newlyAdded.push(added);
      }
      this.enqueueSpecialsIn(new Set(newlyAdded.map(coordKey)), queue, activated);
    }

    if (activationCount > 0) {
      events.push({ kind: 'cascade', amount: activationCount, cascade: this.cascade, label: 'power chain' });
    }
    this.clearPlannedCells(toClear, events);
  }

  private addSpecialPattern(special: SpecialKind, origin: Coord, target: Set<string>): void {
    if (special === 'beamH') {
      this.addRow(origin.r, target);
      return;
    }
    if (special === 'beamV') {
      this.addColumn(origin.c, target);
      return;
    }
    if (special === 'nova') {
      this.addRadius(origin, 1, target);
      return;
    }
    if (special === 'burst') {
      this.addRadius(origin, 1, target);
      for (const direction of DIRECTIONS) {
        this.addToPlan(target, { r: origin.r + direction.r * 2, c: origin.c + direction.c * 2 });
      }
    }
  }

  private addCombinationPattern(
    first: SpecialKind,
    second: SpecialKind,
    a: Coord,
    b: Coord,
    target: Set<string>,
  ): void {
    const kinds = [first, second];
    const hasHorizontal = kinds.includes('beamH');
    const hasVertical = kinds.includes('beamV');
    const hasBeam = hasHorizontal || hasVertical;
    const hasNova = kinds.includes('nova');
    const hasBurst = kinds.includes('burst');

    if (hasBeam && hasHorizontal && hasVertical) {
      for (const row of [a.r - 1, a.r, a.r + 1, b.r - 1, b.r, b.r + 1]) this.addRow(row, target);
      for (const column of [a.c - 1, a.c, a.c + 1, b.c - 1, b.c, b.c + 1]) this.addColumn(column, target);
      return;
    }
    if (hasBeam) {
      const beamHorizontal = hasHorizontal;
      const center = beamHorizontal ? a.r : a.c;
      for (const offset of [-1, 0, 1]) {
        if (beamHorizontal) this.addRow(center + offset, target);
        else this.addColumn(center + offset, target);
      }
      this.addRadius(b, hasNova || hasBurst ? 2 : 1, target);
      return;
    }
    if (hasNova && hasBurst) {
      this.addRadius(a, 2, target);
      this.addRadius(b, 2, target);
      return;
    }
    if (hasNova) {
      this.addRadius(a, 2, target);
      this.addRadius(b, 2, target);
      return;
    }
    if (hasBurst) {
      this.addRadius(a, 2, target);
      this.addRadius(b, 2, target);
      for (const direction of DIRECTIONS) {
        this.addToPlan(target, { r: a.r + direction.r * 3, c: a.c + direction.c * 3 });
        this.addToPlan(target, { r: b.r + direction.r * 3, c: b.c + direction.c * 3 });
      }
      return;
    }
    this.addRadius(a, 1, target);
    this.addRadius(b, 1, target);
  }

  private combinationLabel(first: SpecialKind, second: SpecialKind): string {
    const pair = [first, second].sort().join('+');
    const labels: Record<string, string> = {
      'beamH+beamH': 'parallel horizon sweep',
      'beamV+beamV': 'parallel vertical sweep',
      'beamH+beamV': 'crosswind lattice',
      'beamH+nova': 'horizon nova array',
      'beamV+nova': 'vertical nova array',
      'beamH+burst': 'horizon rupture',
      'beamV+burst': 'vertical rupture',
      'burst+nova': 'double-ring detonation',
      'nova+nova': 'twin nova bloom',
      'burst+burst': 'orbit supernova',
    };
    return labels[pair] ?? `${first} + ${second} resonance`;
  }

  private clearPlannedCells(target: Set<string>, events: MoveEvent[]): void {
    const sorted = Array.from(target)
      .map(parseCoordKey)
      .filter((coord): coord is Coord => coord !== null && this.isInside(coord))
      .sort((a, b) => a.r - b.r || a.c - b.c);
    const impactedPieces: Coord[] = [];
    let cleared = 0;
    const clearedTypes = new Map<PieceType, number>();

    for (const coord of sorted) {
      const cell = this.cells[coord.r][coord.c] as InternalCell;
      if (!cell.active) continue;
      const piece = this.pieces[coord.r][coord.c];
      if (piece) {
        impactedPieces.push(cloneCoord(coord));
        cleared += 1;
        clearedTypes.set(piece.type, (clearedTypes.get(piece.type) ?? 0) + 1);
        this.recordPieceClear(piece, events);
        this.pieces[coord.r][coord.c] = null;
      }

      this.damageCell(coord, events);
      if (cell.hazard > 0 && piece) {
        cell.hazard = Math.max(0, cell.hazard - 1);
        events.push({ kind: 'hazard', cells: [cloneCoord(coord)], amount: -1, label: 'hazard thinned' });
      }
    }

    // A clear also chips neighbouring layered cells.  De-duplicate impacts so
    // one large beam cannot damage the same neighbour once per pixel/event.
    const adjacentTargets = new Set<string>();
    for (const coord of impactedPieces) {
      for (const neighbor of this.neighbors(coord)) {
        const key = coordKey(neighbor);
        if (!target.has(key)) adjacentTargets.add(key);
      }
    }
    for (const key of adjacentTargets) {
      const coord = parseCoordKey(key);
      if (coord) this.damageCell(coord, events);
    }

    if (cleared > 0) {
      const multiplier = Math.max(1, this.cascade);
      this.awardScore(cleared * 10 * multiplier, events);
      events.push({
        kind: 'clear',
        cells: sorted.map(cloneCoord),
        amount: cleared,
        cascade: this.cascade,
        label: clearedTypes.size > 0 ? `${cleared} pieces cleared` : 'clear',
      });
    } else if (sorted.length > 0) {
      events.push({ kind: 'clear', cells: sorted.map(cloneCoord), amount: 0, cascade: this.cascade });
    }
  }

  private recordPieceClear(piece: Piece, events: MoveEvent[]): void {
    const matchesTarget = (objective: ObjectiveState): boolean => this.pieceMatchesObjective(piece, objective);
    this.updateObjective('clear', 1, events, matchesTarget);
    this.updateObjective('collect', 1, events, matchesTarget);
  }

  private damageCell(coord: Coord, events: MoveEvent[]): void {
    if (!this.isInside(coord)) return;
    const cell = this.cells[coord.r][coord.c] as InternalCell;
    if (!cell.active) return;

    if (cell.layers > 0 || cell.blocker !== 'none') {
      const beforeLayers = cell.layers;
      if (cell.layers <= 0) cell.layers = 1;
      cell.layers = Math.max(0, cell.layers - 1);
      const destroyed = cell.layers === 0;
      if (destroyed) {
        cell.blocker = 'none';
        if (!cell.explicitLocked) cell.locked = false;
      }
      this.updateObjective('blocker', 1, events);
      events.push({
        kind: 'damage',
        cells: [cloneCoord(coord)],
        amount: 1,
        label: destroyed ? 'blocker broken' : `blocker chipped (${Math.max(0, beforeLayers - 1)} left)`,
      });
    }
  }

  private awardScore(amount: number, events: MoveEvent[]): void {
    const safeAmount = Math.max(0, Math.floor(amount));
    if (safeAmount === 0) return;
    this.score += safeAmount;
    this.updateObjective('score', safeAmount, events);
  }

  private updateObjective(
    kind: ObjectiveState['kind'],
    amount: number,
    events: MoveEvent[],
    predicate?: (objective: ObjectiveState) => boolean,
  ): void {
    if (amount <= 0) return;
    for (const objective of this.objectives) {
      if (objective.kind !== kind || objective.complete) continue;
      if (predicate && !predicate(objective)) continue;
      const previous = objective.current;
      objective.current = Math.min(objective.target, objective.current + amount);
      objective.complete = objective.current >= objective.target;
      if (objective.current !== previous) {
        events.push({
          kind: 'objective',
          label: objective.complete ? `${objective.label} complete` : objective.label,
          amount: objective.current - previous,
          color: objective.id,
        });
      }
    }
  }

  private pieceMatchesObjective(piece: Piece, objective: ObjectiveState): boolean {
    if (objective.pieceType !== undefined && piece.type !== objective.pieceType) return false;
    if (objective.color !== undefined) {
      const color = objective.color.toLowerCase();
      const paletteColor = PIECE_COLORS[piece.type].toLowerCase();
      if (color !== paletteColor && color !== String(piece.type) && color !== String(piece.type + 1)) return false;
    }
    return true;
  }

  private choosePrismTarget(preferred?: PieceType): PieceType {
    if (preferred !== undefined && isPieceType(preferred)) return preferred;
    const counts = new Map<PieceType, number>();
    for (const cell of this.activeCoordinates()) {
      const piece = this.pieces[cell.r][cell.c];
      if (piece?.special === SPECIAL_NONE) counts.set(piece.type, (counts.get(piece.type) ?? 0) + 1);
    }
    let best: PieceType = 0;
    let bestCount = -1;
    for (const type of PIECE_TYPES) {
      const count = counts.get(type) ?? 0;
      if (count > bestCount) {
        best = type;
        bestCount = count;
      }
    }
    return best;
  }

  private enqueueSpecialsIn(coords: Set<string>, queue: Coord[], alreadyActivated: Set<string>): void {
    for (const key of coords) {
      const coord = parseCoordKey(key);
      if (!coord || alreadyActivated.has(key) || !this.isInside(coord)) continue;
      const piece = this.pieces[coord.r][coord.c];
      if (piece?.special !== SPECIAL_NONE) queue.push(cloneCoord(coord));
    }
  }

  private addEveryPiece(target: Set<string>): void {
    for (const cell of this.activeCoordinates()) {
      if (this.pieces[cell.r][cell.c]) target.add(coordKey(cell));
    }
  }

  private addRow(row: number, target: Set<string>): void {
    if (row < 0 || row >= this.height) return;
    for (let c = 0; c < this.width; c += 1) this.addToPlan(target, { r: row, c });
  }

  private addColumn(column: number, target: Set<string>): void {
    if (column < 0 || column >= this.width) return;
    for (let r = 0; r < this.height; r += 1) this.addToPlan(target, { r, c: column });
  }

  private addRadius(origin: Coord, radius: number, target: Set<string>): void {
    for (let r = origin.r - radius; r <= origin.r + radius; r += 1) {
      for (let c = origin.c - radius; c <= origin.c + radius; c += 1) {
        this.addToPlan(target, { r, c });
      }
    }
  }

  private addToPlan(target: Set<string>, coord: Coord): void {
    if (this.isInside(coord) && this.cells[coord.r][coord.c].active) target.add(coordKey(coord));
  }

  private settleBoard(events: MoveEvent[]): void {
    // Portal and conveyor networks may be cyclic.  A piece is allowed to use
    // each transport system once per settle phase, which prevents a legal
    // A->B->C loop from becoming an unbounded logical animation.
    const teleported = new Set<number>();
    const conveyed = new Set<number>();
    for (let pass = 0; pass < MAX_SETTLE_PASSES; pass += 1) {
      let changed = false;
      changed = this.applyGravity(events) || changed;
      changed = this.applyPortals(events, teleported) || changed;
      changed = this.applyConveyors(events, conveyed) || changed;
      changed = this.spawnPieces(events) || changed;
      const dropped = this.recordDrops(events);
      changed = dropped || changed;
      if (!changed) break;
    }
  }

  private applyGravity(events: MoveEvent[]): boolean {
    let moved = false;
    for (let c = 0; c < this.width; c += 1) {
      let segment: Coord[] = [];
      const flush = (): void => {
        if (segment.length === 0) return;
        const sourcePieces: { piece: Piece; coord: Coord }[] = [];
        for (const coord of segment) {
          const piece = this.pieces[coord.r][coord.c];
          if (piece) sourcePieces.push({ piece, coord: cloneCoord(coord) });
          this.pieces[coord.r][coord.c] = null;
        }

        const targets = segment.slice().reverse();
        for (let index = 0; index < sourcePieces.length; index += 1) {
          const source = sourcePieces[sourcePieces.length - 1 - index];
          const destination = targets[index];
          this.pieces[destination.r][destination.c] = source.piece;
          if (source.coord.r !== destination.r || source.coord.c !== destination.c) {
            moved = true;
            events.push({
              kind: 'spawn',
              from: source.coord,
              to: cloneCoord(destination),
              cells: [source.coord, cloneCoord(destination)],
              label: 'gravity',
            });
          }
        }
        segment = [];
      };

      for (let r = 0; r < this.height; r += 1) {
        const coord = { r, c };
        if (this.isMovableCell(coord)) segment.push(coord);
        else flush();
      }
      flush();
    }
    return moved;
  }

  private applyPortals(events: MoveEvent[], teleported: Set<number>): boolean {
    const groups = new Map<string, Coord[]>();
    for (const cell of this.activeCoordinates()) {
      const portal = this.cells[cell.r][cell.c].portal;
      if (!portal) continue;
      const list = groups.get(portal) ?? [];
      list.push(cell);
      groups.set(portal, list);
    }

    let moved = false;
    for (const portalCells of groups.values()) {
      if (portalCells.length < 2) continue;
      portalCells.sort((a, b) => a.r - b.r || a.c - b.c);
      const proposals: { from: Coord; to: Coord; piece: Piece }[] = [];
      for (let index = 0; index < portalCells.length; index += 1) {
        const from = portalCells[index];
        const to = portalCells[(index + 1) % portalCells.length];
        const piece = this.pieces[from.r][from.c];
        if (
          !piece ||
          teleported.has(piece.id) ||
          !this.isMovableCell(to) ||
          this.pieces[to.r][to.c] !== null
        ) continue;
        proposals.push({ from, to, piece });
      }
      const destinations = new Set(proposals.map((proposal) => coordKey(proposal.to)));
      if (destinations.size !== proposals.length) continue;
      for (const proposal of proposals) {
        this.pieces[proposal.from.r][proposal.from.c] = null;
      }
      for (const proposal of proposals) {
        this.pieces[proposal.to.r][proposal.to.c] = proposal.piece;
        teleported.add(proposal.piece.id);
        moved = true;
        events.push({
          kind: 'spawn',
          from: cloneCoord(proposal.from),
          to: cloneCoord(proposal.to),
          cells: [cloneCoord(proposal.from), cloneCoord(proposal.to)],
          label: 'portal transfer',
        });
      }
    }
    return moved;
  }

  private applyConveyors(events: MoveEvent[], conveyed: Set<number>): boolean {
    const proposals: { from: Coord; to: Coord; piece: Piece }[] = [];
    const destinations = new Set<string>();
    for (const from of this.activeCoordinates()) {
      const piece = this.pieces[from.r][from.c];
      const direction = this.cells[from.r][from.c].conveyor;
      if (!piece || conveyed.has(piece.id) || !direction) continue;
      const to = {
        r: from.r + (direction === 'up' ? -1 : direction === 'down' ? 1 : 0),
        c: from.c + (direction === 'left' ? -1 : direction === 'right' ? 1 : 0),
      };
      if (!this.isMovableCell(to) || this.pieces[to.r][to.c] !== null) continue;
      const key = coordKey(to);
      if (destinations.has(key)) continue;
      destinations.add(key);
      proposals.push({ from, to, piece });
    }

    for (const proposal of proposals) this.pieces[proposal.from.r][proposal.from.c] = null;
    for (const proposal of proposals) {
      this.pieces[proposal.to.r][proposal.to.c] = proposal.piece;
      conveyed.add(proposal.piece.id);
      events.push({
        kind: 'spawn',
        from: cloneCoord(proposal.from),
        to: cloneCoord(proposal.to),
        cells: [cloneCoord(proposal.from), cloneCoord(proposal.to)],
        label: 'conveyor',
      });
    }
    return proposals.length > 0;
  }

  private spawnPieces(events: MoveEvent[]): boolean {
    const explicitSpawns = this.activeCoordinates().filter((coord) => this.cells[coord.r][coord.c].spawn === true);
    const hasExplicitSpawns = explicitSpawns.length > 0;
    let spawned = false;

    for (const segment of this.gravitySegments()) {
      const empty = segment.filter((coord) => this.pieces[coord.r][coord.c] === null);
      if (empty.length === 0) continue;
      const spawnCells = hasExplicitSpawns
        ? empty.filter((coord) => this.cells[coord.r][coord.c].spawn === true)
        : [empty[0]];
      for (const coord of spawnCells) {
        const piece = this.newPiece(this.randomPieceType());
        this.pieces[coord.r][coord.c] = piece;
        spawned = true;
        events.push({ kind: 'spawn', cells: [cloneCoord(coord)], to: cloneCoord(coord), label: 'new piece' });
      }
    }
    return spawned;
  }

  private recordDrops(events: MoveEvent[]): boolean {
    let dropped = false;
    const hasActiveDropObjective = this.objectives.some(
      (objective) => objective.kind === 'drop' && !objective.complete,
    );
    if (!hasActiveDropObjective) return false;

    for (const coord of this.activeCoordinates()) {
      const cell = this.cells[coord.r][coord.c];
      if (!cell.exit) continue;
      const piece = this.pieces[coord.r][coord.c];
      if (!piece || piece.collected) continue;
      const matching = this.objectives.some(
        (objective) => objective.kind === 'drop' && !objective.complete && this.pieceMatchesObjective(piece, objective),
      );
      if (matching) {
        piece.collected = true;
        this.updateObjective('drop', 1, events, (objective) => this.pieceMatchesObjective(piece, objective));
        events.push({ kind: 'objective', cells: [cloneCoord(coord)], amount: 1, label: 'object dropped' });
      }
      // Non-designated pieces also leave through an exit so they cannot plug
      // an object lane while the player is waiting for the target type.
      this.pieces[coord.r][coord.c] = null;
      dropped = true;
    }
    return dropped;
  }

  private updateHazards(events: MoveEvent[]): void {
    const additions: Coord[] = [];
    for (const source of this.activeCoordinates()) {
      if (this.cells[source.r][source.c].hazard <= 0) continue;
      for (const neighbor of this.neighbors(source)) {
        const cell = this.cells[neighbor.r][neighbor.c] as InternalCell;
        if (cell.active && cell.hazard === 0) additions.push(neighbor);
      }
    }

    const unique = new Map<string, Coord>();
    for (const coord of additions) unique.set(coordKey(coord), coord);
    for (const coord of unique.values()) {
      const cell = this.cells[coord.r][coord.c] as InternalCell;
      cell.hazard = 1;
      this.updateObjective('spread', 1, events);
      events.push({ kind: 'hazard', cells: [cloneCoord(coord)], amount: 1, label: 'hazard spread' });
    }
  }

  private gravitySegments(): Coord[][] {
    const segments: Coord[][] = [];
    for (let c = 0; c < this.width; c += 1) {
      let segment: Coord[] = [];
      const flush = (): void => {
        if (segment.length > 0) segments.push(segment);
        segment = [];
      };
      for (let r = 0; r < this.height; r += 1) {
        const coord = { r, c };
        if (this.isMovableCell(coord)) segment.push(coord);
        else flush();
      }
      flush();
    }
    return segments;
  }

  private reshuffleInternal(events: MoveEvent[]): boolean {
    const movable = this.activeCoordinates().filter((coord) => this.isMovableCell(coord));
    const pieces = movable.map((coord) => this.pieces[coord.r][coord.c]).filter((piece): piece is Piece => piece !== null);
    if (pieces.length < 2) return false;

    const original = pieces.slice();
    for (let attempt = 0; attempt < MAX_RESHUFFLE_ATTEMPTS; attempt += 1) {
      const candidate = original.slice();
      this.shuffle(candidate);
      this.assignPieces(movable, candidate);
      if (this.findMatchGroups().length === 0 && this.hasValidMoves()) {
        events.push({ kind: 'reshuffle', amount: attempt + 1, label: 'board re-tuned' });
        return true;
      }
    }

    // Preserve blockers and objectives even when the original multiset cannot
    // produce a playable permutation on a constrained shape.  Only the piece
    // types/specials are repaired; ids remain stable for replay diagnostics.
    for (let attempt = 0; attempt < MAX_RESHUFFLE_ATTEMPTS; attempt += 1) {
      const candidate = movable.map((coord, index) => {
        const source = original[index % original.length];
        return {
          ...source,
          type: this.randomPieceType(),
          special: SPECIAL_NONE,
          collected: false,
        };
      });
      this.assignPieces(movable, candidate);
      if (this.findMatchGroups().length === 0 && this.hasValidMoves()) {
        events.push({ kind: 'reshuffle', amount: MAX_RESHUFFLE_ATTEMPTS + attempt + 1, label: 'board repaired' });
        return true;
      }
    }

    this.assignPieces(movable, original);
    return false;
  }

  private assignPieces(coords: Coord[], pieces: Piece[]): void {
    for (const coord of coords) this.pieces[coord.r][coord.c] = null;
    for (let index = 0; index < coords.length; index += 1) {
      this.pieces[coords[index].r][coords[index].c] = pieces[index] ?? null;
    }
  }

  private repairResidualMatches(events: MoveEvent[] = []): void {
    for (let pass = 0; pass < MAX_RESIDUAL_REPAIR_PASSES; pass += 1) {
      const groups = this.findMatchGroups();
      if (groups.length === 0) return;
      const cells = new Set<string>();
      for (const group of groups) for (const coord of group.cells) cells.add(coordKey(coord));
      const ordered = Array.from(cells)
        .map(parseCoordKey)
        .filter((coord): coord is Coord => coord !== null)
        .sort((a, b) => a.r - b.r || a.c - b.c);
      for (const coord of ordered) {
        const piece = this.pieces[coord.r][coord.c];
        if (!piece || !this.canHoldPiece(coord)) continue;
        const choices = this.shuffledPieceTypes();
        const replacement = choices.find((type) => !this.wouldCreateMatch(coord, type)) ?? choices[0];
        piece.type = replacement;
        piece.special = SPECIAL_NONE;
      }
    }

    if (this.findMatchGroups().length > 0) {
      // Last-resort deterministic cleanup.  This branch is only reachable for
      // pathological one-cell-wide/locked shapes, and prevents unresolved
      // matches from leaking into the renderer.
      for (const group of this.findMatchGroups()) {
        for (const coord of group.cells) {
          if (this.canHoldPiece(coord)) this.pieces[coord.r][coord.c] = null;
        }
      }
      this.settleBoard(events);
    }
  }

  private canAttemptSwap(from: Coord, to: Coord): boolean {
    return (
      this.movesRemaining > 0 &&
      this.isSwappableCell(from) &&
      this.isSwappableCell(to) &&
      Math.abs(from.r - to.r) + Math.abs(from.c - to.c) === 1
    );
  }

  private swapReferences(from: Coord, to: Coord): void {
    const piece = this.pieces[from.r][from.c];
    this.pieces[from.r][from.c] = this.pieces[to.r][to.c];
    this.pieces[to.r][to.c] = piece;
  }

  private isInside(coord: Coord): boolean {
    return coord.r >= 0 && coord.r < this.height && coord.c >= 0 && coord.c < this.width;
  }

  private isMatchableCell(coord: Coord): boolean {
    return this.isInside(coord) && this.cells[coord.r][coord.c].active;
  }

  private canHoldPiece(coord: Coord): boolean {
    return this.isInside(coord) && this.cells[coord.r][coord.c].active && this.cells[coord.r][coord.c].blocker !== 'meteor';
  }

  private isMovableCell(coord: Coord): boolean {
    return this.canHoldPiece(coord) && !this.cells[coord.r][coord.c].locked;
  }

  private isSwappableCell(coord: Coord): boolean {
    return this.isMovableCell(coord) && this.pieces[coord.r][coord.c] !== null;
  }

  private neighbors(coord: Coord): Coord[] {
    return DIRECTIONS
      .map((direction) => ({ r: coord.r + direction.r, c: coord.c + direction.c }))
      .filter((neighbor) => this.isInside(neighbor) && this.cells[neighbor.r][neighbor.c].active);
  }

  private activeCoordinates(): Coord[] {
    const result: Coord[] = [];
    for (let r = 0; r < this.height; r += 1) {
      for (let c = 0; c < this.width; c += 1) {
        if (this.cells[r][c].active) result.push({ r, c });
      }
    }
    return result;
  }

  private countMovablePieces(): number {
    let count = 0;
    for (const coord of this.activeCoordinates()) {
      if (this.isMovableCell(coord) && this.pieces[coord.r][coord.c]) count += 1;
    }
    return count;
  }

  private randomPieceType(): PieceType {
    return PIECE_TYPES[Math.floor(this.nextRandom() * PIECE_TYPES.length)];
  }

  private newPiece(type: PieceType): Piece {
    return { id: this.nextPieceId++, type, special: SPECIAL_NONE, age: 0 };
  }

  private advancePieceAges(): void {
    for (const row of this.pieces) {
      for (const piece of row) {
        if (piece) piece.age = Math.min(0x7fffffff, piece.age + 1);
      }
    }
  }

  private normalizePieceInput(input: PieceType | Piece): Piece {
    if (isPieceType(input)) return this.newPiece(input);
    const type = isPieceType(input.type) ? input.type : 0;
    const special = SPECIAL_KINDS.includes(input.special) ? input.special : SPECIAL_NONE;
    return {
      id: Number.isFinite(input.id) ? Math.max(1, Math.floor(input.id)) : this.nextPieceId++,
      type,
      special,
      age: Number.isFinite(input.age) ? Math.max(0, Math.floor(input.age)) : 0,
      ...(input.collected === undefined ? {} : { collected: Boolean(input.collected) }),
    };
  }

  private copyPiece(piece: Piece | null): Piece | null {
    return piece ? { ...piece } : null;
  }

  private copyCellForSnapshot(cell: CellState): CellState {
    const internal = cell as InternalCell;
    const copy: CellState = { ...cell };
    delete (copy as Partial<InternalCell>).explicitLocked;
    // Avoid serializing accidental undefined fields differently between runs.
    if (copy.portal === undefined) delete copy.portal;
    if (copy.conveyor === undefined) delete copy.conveyor;
    if (copy.spawn === undefined) delete copy.spawn;
    if (copy.exit === undefined) delete copy.exit;
    void internal;
    return copy;
  }

  private captureInternalState(): InternalState {
    return {
      cells: this.cells.map((row) => row.map((cell) => ({ ...(cell as InternalCell) }))),
      pieces: this.pieces.map((row) => row.map((piece) => this.copyPiece(piece))),
      objectives: this.objectives.map((objective) => ({ ...objective })),
      score: this.score,
      movesRemaining: this.movesRemaining,
      cascade: this.cascade,
      rngState: this.rngState,
      nextPieceId: this.nextPieceId,
    };
  }

  private restoreInternalState(state: InternalState): void {
    this.cells = state.cells.map((row) => row.map((cell) => ({ ...cell })));
    this.pieces = state.pieces.map((row) => row.map((piece) => this.copyPiece(piece)));
    this.objectives = state.objectives.map((objective) => ({ ...objective }));
    this.score = state.score;
    this.movesRemaining = state.movesRemaining;
    this.cascade = state.cascade;
    this.rngState = state.rngState;
    this.nextPieceId = state.nextPieceId;
    this.specialComboCount = 0;
  }

  private nextRandom(): number {
    let value = this.rngState | 0;
    value ^= value << 13;
    value ^= value >>> 17;
    value ^= value << 5;
    this.rngState = value >>> 0;
    if (this.rngState === 0) this.rngState = 0x6d2b79f5;
    return this.rngState / 0x100000000;
  }

  private shuffle<T>(items: T[]): void {
    for (let index = items.length - 1; index > 0; index -= 1) {
      const swapIndex = Math.floor(this.nextRandom() * (index + 1));
      const value = items[index];
      items[index] = items[swapIndex];
      items[swapIndex] = value;
    }
  }
}

export default BoardEngine;
